import apiClient from "src/utils/apiClient";
import type {
  UseCaseConfig,
  WorkspaceTask,
  WorkspaceResult,
  PermissionsMember,
  RoleValue,
} from "src/components/pages/auditModule/shared/types";
import { ROLE, ROLE_BY_API, API_ROLE } from "src/components/pages/auditModule/shared/types";

//TODO: Replace placeholder endpoints with real backend routes once Audit V2 APIs are implemented.
export interface AuditModuleTasksResponse {
  tasks: Array<{
    id: string;
    use_case_id: string;
    status: string;
    assigned_to: string | null;
    priority: number;
    created_at: string;
  }>;
}

export interface AuditModuleResultsResponse {
  results: Array<{
    id: string;
    task_id: string;
    auditor_id: string;
    status: string;
    decisions: Record<string, { decision: string }>;
  }>;
}

export interface AuditModuleUseCaseConfigsResponse {
  configs: UseCaseConfig[];
}

export interface AuditModuleSingleConfigResponse {
  config: UseCaseConfig;
}

export interface AuditModuleWorkspaceTaskResponse {
  task: WorkspaceTask | null;
  config: UseCaseConfig | null;
  result: WorkspaceResult | null;
  allTasks: WorkspaceTask[];
}

export interface AuditModuleWorkspaceResultResponse extends WorkspaceResult {}

// Body of POST /vlm_audit/sampling/{preview,run} (backend SamplingConfig).
// startDate/endDate are inclusive UTC YYYY-MM-DD bounds on the inspection's completion time.
export interface SamplingRunConfig {
  useCaseId: string;
  startDate: string;
  endDate: string;
  // % (1-100) of the records in the range to import; 100 imports all of them.
  samplePercent: number;
  seed?: number;
}

// Backend SamplingRunResult. allocation is the drawn record count per site (siteCode -> count).
// On a preview, ingested/auditorsUpdated/skipped are what a submit would do (new records /
// existing samples with no auditors that would get them / the rest); failed and batchId are null.
export interface SamplingRunResult {
  population: number;
  sampleSize: number;
  allocation: Record<string, number>;
  ingested: number | null;
  failed: number | null;
  skipped: number | null;
  auditorsUpdated?: number | null;
  batchId: string | null;
  // submit only: new records assigned per auditor alias.
  assignedAuditorCounts?: Record<string, number>;
}

// Raw shape returned by GET /audit_access/me (backend get_my_permission).
// The role is the caller's designation in the DynamoDB permissions registry
// (an LDAP audit-admin resolves to "Admin" even without a registry row).
export interface MyAuditPermission {
  role: string | null; // "Admin" | "Program Owner" | "Auditor" | null
  canManageMembers: boolean;
}

// Raw shape returned by GET /audit_access/my_use_cases.
export interface MyAuditUseCases {
  isAdmin: boolean;
  // useCaseId -> per-use-case role ("Admin" = owns/configures it, "Auditor" = audits it)
  useCaseRoles: Record<string, string>;
}

// Raw shape returned by the audit_access members API (backend PermissionMemberView).
interface PermissionMemberView {
  alias: string;
  displayName: string | null;
  role: string; // "Admin" | "Program Owner" | "Auditor"
  authorizedUseCases: string[];
  elevated: boolean;
  assignableRoles?: string[];
}

function toPermissionsMember(v: PermissionMemberView): PermissionsMember {
  return {
    login: v.alias,
    name: v.displayName || v.alias,
    role: ROLE_BY_API[v.role] ?? ROLE.AUDITOR,
    assigned_use_case_ids: v.authorizedUseCases ?? [],
    elevated: v.elevated,
    assignable_roles: (v.assignableRoles ?? [])
      .map((r) => ROLE_BY_API[r])
      .filter((r): r is RoleValue => Boolean(r)),
  };
}

class AuditModuleService {
  async getTasks(): Promise<AuditModuleTasksResponse> {
    const { data } = await apiClient.get("/audit_queue/tasks");
    return data;
  }

  async getResults(taskIds: string[]): Promise<AuditModuleResultsResponse> {
    const { data } = await apiClient.post("/audit_queue/results", { task_ids: taskIds });
    return data;
  }

  // ----------------------------  Config Endpoints  ----------------------------

  async getUseCaseConfigs(): Promise<AuditModuleUseCaseConfigsResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases");
    return data;
  }

  async getUseCaseConfig(useCaseId: string): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases/" + useCaseId);
    return data;
  }

  async createUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.post("/audit_config/use_cases", config);
    return data;
  }

  async updateUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.put("/audit_config/use_cases/" + config.id, config);
    return data;
  }

  // ----------------------------  Sampling Endpoints  ----------------------------

  async previewSampling(config: SamplingRunConfig): Promise<SamplingRunResult> {
    const { data } = await apiClient.post<SamplingRunResult>("/vlm_audit/sampling/preview", config);
    return data;
  }

  async submitSampling(config: SamplingRunConfig): Promise<SamplingRunResult> {
    const { data } = await apiClient.post<SamplingRunResult>("/vlm_audit/sampling/run", config);
    return data;
  }

  // ----------------------------  Permissions / Access Endpoints  ----------------------------

  async getMyPermission(): Promise<MyAuditPermission> {
    const { data } = await apiClient.get<MyAuditPermission>("/audit_access/me");
    return data;
  }

  async getMyUseCases(): Promise<MyAuditUseCases> {
    const { data } = await apiClient.get<MyAuditUseCases>("/audit_access/my_use_cases");
    return data;
  }

  async getPermissionMembers(): Promise<PermissionsMember[]> {
    const { data } = await apiClient.get<PermissionMemberView[]>("/audit_access/members");
    return data.map(toPermissionsMember);
  }

  async updateMemberRole(alias: string, role: RoleValue): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>("/audit_access/members/" + alias, {
      role: API_ROLE[role],
    });
    return toPermissionsMember(data);
  }

  async setMemberUseCases(alias: string, useCaseIds: string[]): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>(
      "/audit_access/members/" + alias + "/use_cases",
      { useCaseIds },
    );
    return toPermissionsMember(data);
  }

  // ----------------------------  Workspace Endpoints  ----------------------------

  async getWorkspaceTask(taskId: string): Promise<AuditModuleWorkspaceTaskResponse> {
    const { data } = await apiClient.get("/audit_workspace/tasks/" + taskId);
    return data;
  }

  async saveWorkspaceResult(
    result: WorkspaceResult,
    submit: boolean,
  ): Promise<AuditModuleWorkspaceResultResponse> {
    const { data } = await apiClient.post("/audit_workspace/results", { ...result, submit });
    return data;
  }
}

export default new AuditModuleService();
